/**
 * Putting a downloaded gun where it belongs.
 *
 * Every model in the library arrives in its own units, in its own orientation,
 * around its own origin — some are 5 units long, some 1100, some point up.
 * Working that out at load time is guesswork with no way to check it, and it
 * got it wrong often enough to matter: these exports are all rigged, and a
 * skinned mesh does not report its own size, so any gun whose armature carried
 * a scale measured as a fraction of its real length and turned up in your
 * hands the size of a matchstick.
 *
 * So it is measured offline instead — `tools/audit_weapons.mjs` evaluates the
 * skinning, finds the barrel, finds which way is down, and writes the numbers
 * into `weapons.manifest.json`. All that is left here is applying them.
 *
 * The pose every gun ends up in:
 *   - muzzle down -Z, sights up +Y, ejection side +X
 *   - the origin at the back of the weapon, on the barrel line, centred across
 *   - one unit is one metre, and the length is the length of the real gun
 *
 * Anchoring at the back rather than the middle is what makes a pistol and an
 * anti-materiel rifle both look right in the same rest pose: the part nearest
 * your face is always in the same place, and a longer gun simply reaches
 * further into the screen. That is how every shooter frames it.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import manifest from './weapons.manifest.json' with { type: 'json' };
import overrides from './weapons.overrides.json' with { type: 'json' };

/**
 * Where the baked models live, and the loader that can read them.
 *
 * The collection ships compressed — a gun is a megabyte and a half as it
 * comes out of the modelling package and about a fifth of that once quantised,
 * which over four hundred and fifty of them is the difference between a
 * shippable build and an unshippable one. That costs one decoder, set here so
 * nothing downstream can forget it and get a silently empty gun.
 *
 * Absolute, because the game is served from /game/ and a relative path would
 * look for the models underneath that instead of at the site root.
 */
export const GUN_BASE = '/models/weapons/';
export const gunLoader = new GLTFLoader();
gunLoader.setMeshoptDecoder(MeshoptDecoder);

export interface GunEntry {
  file: string;
  uid: string;
  title: string;
  author: string;
  source: string | null;
  license: string;
  cls: string;
  tris: number;
  /** Which model axis is the barrel, which is up, which is across. */
  forward: number;
  up: number;
  side: number;
  /** +1 when the muzzle is at the high end of that axis. */
  dirF: number;
  dirU: number;
  raw: [number, number, number];
  scale: number;
  length: number;
  height: number;
  width: number;
  /** The anchor in raw model coordinates, as (along barrel, up, across). */
  origin: [number, number, number];
  /**
   * Where the flash comes out, in posed metres.
   *
   * Measured per gun by `tools/anchors.mjs` rather than assumed to be the
   * middle of the front of the bounding box, which is what it was and which is
   * wrong for anything whose bore is not centred or whose frontmost geometry
   * is not the barrel — a scope, a folded bipod, the lower of two barrels.
   */
  muzzle?: [number, number, number];
  /**
   * Where a right-handed shooter's hands go, in posed metres.
   *
   * `grip` is the trigger hand, `support` the off hand. Measured per gun for
   * the same reason the muzzle is: a player model has to hold four hundred
   * different shapes, and there is no offset from the origin that is right for
   * a bullpup, a revolver and a Bren at once.
   */
  grip?: [number, number, number];
  support?: [number, number, number];
  /**
   * Meshes to leave behind, by index in traversal order.
   *
   * A few of these files hold more than one object — a second gun beside the
   * first, a stand, a display base — and drawing all of it puts two things in
   * your hands, which is exactly the bug this library shipped with.
   */
  drop: number[];
  strays: number;
  ok: boolean;
}

/**
 * Corrections a person made, over the numbers the script measured.
 *
 * Nearly all of the poses come out right, and the ones that do not are the
 * ones no rule can settle: a stubby submachine gun with no stock and a long
 * magazine has more bulk at the front than the back, so "the thin end is the
 * muzzle" points it at your own face. Rather than keep inventing rules for
 * those, `/aim.html` lets somebody look at all of them and flip the ones that
 * are wrong, and what they decide is kept here.
 *
 * Merged at load rather than baked into the manifest, so a correction is live
 * the moment it is saved and re-measuring the library can never undo one.
 */
export interface GunFix {
  /**
   * The drawer this gun belongs in, when the name-based sort got it wrong.
   *
   * Not cosmetic: the class picks the stat profile, the era, and which perk
   * boosts it, so a P90 filed as a pistol is a pistol in every way that
   * matters to a run.
   */
  cls?: string;
  forward?: number;
  up?: number;
  side?: number;
  dirF?: number;
  dirU?: number;
  /** Overrides the length the class profile assumed. */
  length?: number;
  /**
   * Where the flash comes out, in posed metres, placed by hand.
   *
   * The seed is the centroid of the frontmost sliver of geometry, which lands
   * on the bore for most guns and on the wrong thing for the rest. This wins
   * over it.
   */
  muzzle?: [number, number, number];
  /** Where the hands go, placed by hand. Posed metres, right-handed shooter. */
  grip?: [number, number, number];
  support?: [number, number, number];
  /**
   * The gun's length when those three points were placed.
   *
   * They are positions on a gun, in metres, so they are only true at the size
   * the gun was when somebody put them there. Correcting a rifle's length
   * afterwards has to carry them with it or the flash ends up inside the
   * barrel and both hands slide off the back — and silently, since every
   * number still looks reasonable. With this the two jobs can be done in
   * either order, which is the only reason they can be done in one pass.
   */
  atLength?: number;
  /** Somebody has looked at this one. */
  seen?: boolean;
  /** Somebody has looked at where its flash comes out. */
  placed?: boolean;
}

export const GUN_FIXES: Record<string, GunFix> =
  (overrides as unknown as { guns?: Record<string, GunFix> }).guns ?? {};

/**
 * Applying a fix is not just a field copy.
 *
 * `scale` and `origin` were derived from the axes and the length, so changing
 * either has to re-derive them or the gun comes out the right way round and
 * the wrong size, anchored somewhere in the middle of itself.
 */
export function applyFix(base: GunEntry, fix: GunFix): GunEntry {
  const e: GunEntry = { ...base, ...fix };
  const [rawF, rawU, rawS] = base.raw;
  // The raw extents are stored in (forward, up, side) order for the *old*
  // axes, so re-index them if the axes moved.
  const byAxis: Record<number, number> = { [base.forward]: rawF, [base.up]: rawU, [base.side]: rawS };
  const along = byAxis[e.forward] ?? rawF;
  const tall = byAxis[e.up] ?? rawU;
  const across = byAxis[e.side] ?? rawS;
  e.raw = [along, tall, across];
  e.scale = e.length / Math.max(1e-9, along);
  e.height = +(tall * e.scale).toFixed(3);
  e.width = +(across * e.scale).toFixed(3);
  /*
   * Resizing a gun moves everything hung off it.
   *
   * The muzzle and the two hand holds are stored in posed metres, so they are
   * only true at the scale they were placed at. Correcting a rifle from the
   * class default to its real length without this leaves the flash a few
   * centimetres inside the barrel and both hands in the wrong place — and
   * silently, because every number still looks reasonable.
   */
  const move = (p: [number, number, number] | undefined, from: number):
    [number, number, number] | undefined => {
    if (!p || from <= 0) return p;
    const k = e.length / from;
    return k === 1 ? p : [+(p[0] * k).toFixed(4), +(p[1] * k).toFixed(4), +(p[2] * k).toFixed(4)];
  };
  // A placed point scales from the length it was placed at; a measured one
  // from the length it was measured at, which is whatever the manifest says.
  e.muzzle = fix.muzzle ? move(fix.muzzle, fix.atLength ?? base.length) : move(base.muzzle, base.length);
  e.grip = fix.grip ? move(fix.grip, fix.atLength ?? base.length) : move(base.grip, base.length);
  e.support = fix.support ? move(fix.support, fix.atLength ?? base.length) : move(base.support, base.length);
  // The anchor is the back of the weapon on the barrel axis; flipping the
  // muzzle moves it to the other end of the same span.
  if (fix.dirF !== undefined && fix.dirF !== base.dirF) {
    e.origin = [base.origin[0] + base.dirF * along, base.origin[1], base.origin[2]];
  }
  return e;
}

export const GUNS: GunEntry[] = (manifest as unknown as { models: GunEntry[] }).models.map((g) =>
  GUN_FIXES[g.uid] ? applyFix(g, GUN_FIXES[g.uid]) : g,
);
export const GUN_BY_FILE = new Map(GUNS.map((g) => [g.file, g]));
export const GUN_BY_UID = new Map(GUNS.map((g) => [g.uid, g]));

/**
 * Where to put the flash for this gun.
 *
 * Falls back to the old constant — the middle of the front face — for anything
 * that has not been measured yet, so a manifest without muzzles still draws.
 */
export function muzzleOf(e: GunEntry): THREE.Vector3 {
  const m = e.muzzle;
  return m ? new THREE.Vector3(m[0], m[1], m[2]) : new THREE.Vector3(0, 0, -e.length - 0.03);
}

/** The trigger hand, for a right-handed shooter. */
export function gripOf(e: GunEntry): THREE.Vector3 {
  const g = e.grip;
  return g ? new THREE.Vector3(g[0], g[1], g[2]) : new THREE.Vector3(0.012, -0.035, -e.length * 0.22);
}

/** The off hand: out on the handguard, or under the firing hand on a pistol. */
export function supportOf(e: GunEntry): THREE.Vector3 {
  const s = e.support;
  return s ? new THREE.Vector3(s[0], s[1], s[2]) : new THREE.Vector3(-0.012, -0.03, -e.length * 0.6);
}

const axis = (i: number, s: number): THREE.Vector3 =>
  new THREE.Vector3(i === 0 ? s : 0, i === 1 ? s : 0, i === 2 ? s : 0);

/**
 * The matrix that carries a model into the canonical pose.
 *
 * Built as a basis rather than as three Euler angles, because composing
 * rotations by hand is where this kind of code always goes wrong. The side
 * axis takes whichever sign keeps the basis right-handed — a mirrored one
 * would turn the gun inside out and light it from the wrong side.
 */
export function gunMatrix(e: GunEntry): THREE.Matrix4 {
  const f = axis(e.forward, e.dirF);
  const u = axis(e.up, e.dirU);
  let s = axis(e.side, 1);
  const rot = new THREE.Matrix4().makeBasis(s, u, f.clone().negate());
  if (rot.determinant() < 0) {
    s = axis(e.side, -1);
    rot.makeBasis(s, u, f.clone().negate());
  }
  // The basis maps the canonical axes onto the model's; the inverse is what
  // carries the model into the pose.
  rot.invert();

  const origin = new THREE.Vector3();
  origin.setComponent(e.forward, e.origin[0]);
  origin.setComponent(e.up, e.origin[1]);
  origin.setComponent(e.side, e.origin[2]);

  const m = new THREE.Matrix4().makeScale(e.scale, e.scale, e.scale);
  m.multiply(rot);
  m.multiply(new THREE.Matrix4().makeTranslation(-origin.x, -origin.y, -origin.z));
  return m;
}

/**
 * Wrap a freshly-loaded scene so it sits in the canonical pose.
 *
 * The transform goes on a wrapper rather than on the model, so the caller can
 * move, animate and hang things off the result without ever touching the
 * matrix that makes it the right size.
 */
/**
 * Bake the skinning away.
 *
 * Every one of these exports is rigged and none of them animates — a gun does
 * not bend. Left as skinned meshes they cost a bone texture each, and three
 * makes one *per skeleton*: a single rifle on the floor was thirteen textures
 * that nothing ever disposed, so a long run leaked a few hundred of them and
 * eventually the tab died.
 *
 * Since the bones never move, every vertex has one fixed position. Pushing
 * each one through its bones once, here, turns a skinned mesh into a plain
 * one: no bone texture, no skinning in the vertex shader, and bounds that can
 * be measured the ordinary way.
 */
/** Bare steel, and cartridge brass. Both deliberately close to neutral. */
const STEEL = new THREE.Color(0x8d949c);
const BRASS = new THREE.Color(0x9b7f4e);

/**
 * What the magazine says about the gun.
 *
 * The same table the fallback block has always used, which was written for
 * exactly this job and until now only ever painted a placeholder cube. Muted
 * on purpose: these read as *materials* — parkerised, phosphate, walnut,
 * polymer — rather than as team colours.
 */
const CLASS_TINT: Record<string, number> = {
  pistol: 0xb9c2cc, revolver: 0x9a7d5a, machine_pistol: 0xc8b06a, smg: 0x8d949c,
  pdw: 0x7f8fa0, shotgun: 0x6f5a3e, carbine: 0x6e7a63, rifle: 0x6e7a63,
  battle: 0x7a6a52, dmr: 0x5f6f7a, sniper: 0x6b5a3c, antimat: 0x55504a,
  lmg: 0x5c6350, launcher: 0x7f7350, bow: 0x6a7a5c, special: 0x9fd8ff,
  exotic: 0x9fd8ff, grenade: 0x6e7a63,
};

/**
 * The parts worth telling apart, and the joint names that mean each of them.
 *
 * Ordered, because the first match wins and some names contain others:
 * `magrelease` is a lever on the receiver and not the magazine, `boltrelease`
 * likewise, so both have to be caught before `mag` and `bolt` are.
 */
const PARTS: [RegExp, string][] = [
  [/magrelease|boltrelease|release|safety|selector|trigger|hammer|charg/, 'furniture'],
  [/\bmag|magazine|clip|drum|belt/, 'mag'],
  [/slide|bolt|carrier|breech|cylinder|pump|lever/, 'action'],
  [/stock|butt|brace/, 'stock'],
  [/bullet|round|shell|cartridge/, 'brass'],
  [/barrel|muzzle|supp|silenc|can\b/, 'barrel'],
];

/** Whichever joint moves most of this mesh, as one of `PARTS`. */
function dominantJoint(mesh: THREE.SkinnedMesh): string | null {
  const idx = mesh.geometry.attributes.skinIndex;
  const wgt = mesh.geometry.attributes.skinWeight;
  const bones = mesh.skeleton?.bones;
  if (!idx || !wgt || !bones?.length) return null;
  const mass = new Float64Array(bones.length);
  for (let i = 0; i < idx.count; i++) {
    for (let k = 0; k < 4; k++) {
      const b = idx.getComponent(i, k);
      const w = wgt.getComponent(i, k);
      if (w > 0 && b < mass.length) mass[b] += w;
    }
  }
  let best = -1;
  let bestMass = 0;
  for (let b = 0; b < mass.length; b++) if (mass[b] > bestMass) { bestMass = mass[b]; best = b; }
  if (best < 0) return null;
  const name = (bones[best].name || '').toLowerCase();
  for (const [re, part] of PARTS) if (re.test(name)) return part;
  return null;
}

function flatten(root: THREE.Object3D): void {
  const skinned: THREE.SkinnedMesh[] = [];
  root.updateMatrixWorld(true);
  root.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned.push(o as THREE.SkinnedMesh);
  });
  const v = new THREE.Vector3();
  for (const mesh of skinned) {
    const geo = mesh.geometry;
    const src = geo.attributes.position;
    /*
     * Into a fresh float array rather than back into the attribute.
     *
     * These models ship quantised — positions are normalised sixteen-bit
     * integers with the scale on the node — so writing a metre-scale float
     * back into one clamps it to the range and the whole gun collapses into a
     * unit cube. The first version of this did exactly that and turned every
     * rifle into a small brick, which the contact sheet caught.
     */
    /*
     * Which part of the gun this mesh is, taken off the skeleton on the way past.
     *
     * These models are split by material, not by function, and the material
     * names are `Material.001` through `Material.023` — nothing to read there.
     * The *joints* are named though, and by hand: 342 of the library have a
     * `body`, 268 a `mag`, 224 a `bolt`, 39 a `slide`, 39 a `stock`. That is
     * the only functional labelling anywhere in the collection.
     *
     * It has to be read here, in the two lines before the skinning is deleted,
     * because after that the association is gone for good. Weight is what
     * decides it: a mesh belongs to whichever joint moves most of it.
     */
    const part = dominantJoint(mesh);

    const baked = new Float32Array(src.count * 3);
    for (let i = 0; i < src.count; i++) {
      v.fromBufferAttribute(src, i);
      mesh.applyBoneTransform(i, v);
      baked[i * 3] = v.x;
      baked[i * 3 + 1] = v.y;
      baked[i * 3 + 2] = v.z;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(baked, 3));
    geo.deleteAttribute('skinIndex');
    geo.deleteAttribute('skinWeight');
    // The bones rotate as well as move, so the old normals point the wrong
    // way. Recomputed from the faces: gltf-transform's weld keeps a separate
    // vertex either side of a hard edge, so the facets survive.
    geo.deleteAttribute('normal');
    geo.computeVertexNormals();
    geo.computeBoundingBox();
    geo.computeBoundingSphere();

    const plain = new THREE.Mesh(geo, mesh.material);
    plain.name = mesh.name;
    if (part) plain.userData.part = part;
    plain.position.copy(mesh.position);
    plain.quaternion.copy(mesh.quaternion);
    plain.scale.copy(mesh.scale);
    plain.frustumCulled = mesh.frustumCulled;
    mesh.parent?.add(plain);
    mesh.parent?.remove(mesh);
    // The skeleton owns a texture of its own; nothing else will free it.
    mesh.skeleton?.dispose();
  }
}

/**
 * Make four hundred unrelated models look like one game's arsenal.
 *
 * These come from a library by one author but not from one project: some are
 * painted, some are bare metal, some carry a wood albedo, some carry no map at
 * all, and the roughness runs from mirror to chalk. Put fifty of them in the
 * same game and the guns are the least coherent thing in it — which matters
 * more here than it would elsewhere, because the world around them is now one
 * pale material and three colours.
 *
 * So every gun is normalised on the way in. Geometry is untouched — the
 * silhouette is the whole reason to use a real model. Normal maps are kept,
 * because those are surface and surface is what makes graphite look machined.
 * Everything that carries a *colour* is dropped: the albedo map goes, the tint
 * goes, and what comes back is the same shape in the same graphite as every
 * other made thing in the world.
 *
 * A small amount of variation survives, keyed to the model, so an arsenal is
 * not fifty identical greys — but it is variation in how light the metal is,
 * never in what colour it is.
 */
function normalise(root: THREE.Object3D, uid: string, cls?: string): void {
  let h = 2166136261;
  for (let i = 0; i < uid.length; i++) {
    h ^= uid.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const shade = 0.82 + ((h >>> 8) & 255) / 255 * 0.36;
  const body = new THREE.Color(0x1b2129);
  const accent = new THREE.Color(cls ? (CLASS_TINT[cls] ?? 0x8d949c) : 0x8d949c);
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const part = mesh.userData.part as string | undefined;
    /*
     * Black gun, marked parts.
     *
     * The whole arsenal used to be one colour, which read as four hundred
     * silhouettes of the same object. What separates them at a glance is not
     * hue — the game has one accent and it belongs to the map — but *where*
     * the lighter pieces sit: a magazine forward of the hand says rifle, under
     * it says pistol, a long one says SMG. So the receiver stays near-black
     * and the moving parts are lifted out of it, with the magazine carrying
     * the class's own tone so a shotgun and a sniper are not the same picture.
     *
     * Chroma stays low on purpose. These are lit by the map's rig and judged
     * by the same frame gates as everything else; a bright gun would spend the
     * accent budget the map is holding for one door.
     */
    let tone = body;
    let metal = 0.62;
    let rough = 0.36;
    if (part === 'mag') { tone = accent; metal = 0.5; rough = 0.46; }
    else if (part === 'action') { tone = STEEL; metal = 0.8; rough = 0.24; }
    else if (part === 'stock') { tone = body.clone().lerp(accent, 0.35); metal = 0.3; rough = 0.62; }
    else if (part === 'brass') { tone = BRASS; metal = 0.85; rough = 0.3; }
    else if (part === 'barrel') { tone = body.clone().lerp(STEEL, 0.28); metal = 0.75; rough = 0.3; }
    else if (part === 'furniture') { tone = body.clone().lerp(STEEL, 0.45); metal = 0.7; rough = 0.34; }
    const src = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    // Cloned when this mesh is not the body, because the models share
    // materials between meshes and two parts cannot hold one colour.
    const list = part ? src.map((m) => (m as THREE.Material).clone()) : src;
    if (part) mesh.material = Array.isArray(mesh.material) ? (list as THREE.Material[]) : list[0];
    for (const m of list) {
      const mat = m as THREE.MeshStandardMaterial;
      if (!mat || !('color' in mat)) continue;
      // The colour maps go, and so does anything that would tint what is left.
      if (mat.map) {
        mat.map.dispose();
        mat.map = null;
      }
      if (mat.emissiveMap) {
        mat.emissiveMap.dispose();
        mat.emissiveMap = null;
      }
      mat.emissive?.setHex(0x000000);
      mat.vertexColors = false;
      mat.color.copy(tone).multiplyScalar(shade);
      mat.metalness = metal;
      mat.roughness = rough;
      mat.envMapIntensity = 0.9;
      mat.needsUpdate = true;
    }
  });
}

/*
 * A class stripe, on the model, in the class colour.
 *
 * The guns already had a per-class `colour` and it was invisible: it tinted
 * the block stand-in, and the stand-in is hidden the moment the real model
 * arrives. So every weapon in the library looked like the same dark metal and
 * nothing on screen said whether the thing you had just picked up was a
 * sidearm or a marksman rifle.
 *
 * A stripe on the receiver says it. Both flanks, because the view model shows
 * one side and a gun lying on the floor is seen from wherever you walked in; slightly proud of the surface so it is
 * not swallowed by the silhouette; emissive, because the answer has to survive
 * an unlit basement. Sized from the model's own bounds, so it lands on the
 * receiver of a machine pistol and of an anti-materiel rifle alike.
 */
const STRIPE = 'family-stripe';

export function markFamily(obj: THREE.Object3D, colour: number): void {
  for (const old of obj.children.filter((c) => c.name === STRIPE)) {
    obj.remove(old);
    (old as THREE.Mesh).geometry?.dispose();
  }
  const box = new THREE.Box3().setFromObject(obj);
  if (box.isEmpty() || !isFinite(box.min.x)) return;
  const size = box.getSize(new THREE.Vector3());
  const mid = box.getCenter(new THREE.Vector3());

  const long = Math.max(0.035, size.z * 0.12);
  const tall = Math.max(0.008, size.y * 0.07);
  /*
   * Unlit, so the colour is the colour.
   *
   * The first version of this was a standard material with an emissive lift,
   * and the view model's key light is bright enough that every family came out
   * the same pale wash — a blue pistol and a white bow were one colour. An
   * indicator has one job and being subject to the lighting is not part of it.
   */
  const mat = new THREE.MeshBasicMaterial({ color: colour, toneMapped: false });
  const geo = new THREE.BoxGeometry(1, tall, long);
  // A quarter of the way forward from the back: the receiver on nearly
  // everything, rather than a stock on the long guns or thin air on the short.
  const z = box.max.z - size.z * 0.26;
  for (const side of [-1, 1]) {
    const chip = new THREE.Mesh(geo, mat);
    chip.name = STRIPE;
    chip.scale.x = Math.max(0.005, size.x * 0.08);
    chip.position.set(
      side < 0 ? box.min.x + size.x * 0.03 : box.max.x - size.x * 0.03,
      mid.y + size.y * 0.2,
      z,
    );
    chip.frustumCulled = false;
    chip.renderOrder = 1;
    obj.add(chip);
  }
}

export function poseGun(root: THREE.Object3D, e: GunEntry): THREE.Group {
  if (e.drop?.length) {
    const loose = new Set(e.drop);
    const meshes: THREE.Object3D[] = [];
    root.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) meshes.push(o);
    });
    meshes.forEach((m, i) => {
      if (loose.has(i)) m.parent?.remove(m);
    });
  }
  flatten(root);
  normalise(root, e.uid, e.cls);
  const inner = new THREE.Group();
  inner.add(root);
  inner.applyMatrix4(gunMatrix(e));
  const outer = new THREE.Group();
  outer.add(inner);
  outer.name = `gun:${e.uid}`;
  outer.userData.gun = e;
  return outer;
}
